import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { useTheme } from "../../../shared/common/ThemeContext";
import { fetchAuth } from "../../../shared/common/api";
import { useSeason } from "../../../shared/common/SeasonContext";
import ClubCalendar from "../../../shared/common/ClubCalendar";
import UpcomingActivities from "../../../shared/common/UpcomingActivities";
import { useTranslation } from "react-i18next";

const CalendarPage = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { season } = useSeason();

  const [scope, setScope] = useState("global");
  const [players, setPlayers] = useState([]);
  const [playerId, setPlayerId] = useState("");

  // Transfermarkt import
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [calendarKey, setCalendarKey] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchAuth("/players");
        if (!res.ok) throw new Error("Failed to fetch players");
        const data = await res.json();

        const list = data.players || data || [];
        setPlayers(list.filter((p) => p.player_enabled === true));
      } catch (e) {
        toast.error(t("pages.calendar.toast_fetch_error"));
      }
    })();
  }, []);

  useEffect(() => {
    if (scope === "global") setPlayerId("");
  }, [scope]);

  // Auto-sync scores for past matches on page load (silent background call)
  useEffect(() => {
    if (!season) return;
    const seasonId = season.split("-")[0];
    fetchAuth("/calendar/sync-scores", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ season_id: seasonId }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.updated > 0) setCalendarKey((k) => k + 1);
      })
      .catch((err) => console.error("[sync-scores] Error:", err));
  }, [season]);

  const handleImportMatches = useCallback(async () => {
    if (importing) return;
    const seasonId = season.split("-")[0];
    setImporting(true);
    setImportResult(null);
    try {
      const res = await fetchAuth("/calendar/import-matches", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ season_id: seasonId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || t("pages.calendar.toast_import_failed"));

      setImportResult(data);
      if (data.imported > 0 || data.updated > 0 || data.removed > 0) {
        toast.success(t("pages.calendar.toast_synced", {
          imported: data.imported || 0,
          updated: data.updated || 0,
          removed: data.removed || 0,
        }));
        // Force calendar reload
        setCalendarKey((k) => k + 1);
      } else {
        toast(t("pages.calendar.toast_none"), { icon: "ℹ️" });
      }
    } catch (e) {
      toast.error(e.message || t("pages.calendar.toast_import_error"));
    } finally {
      setImporting(false);
    }
  }, [season, importing, t]);

  return (
    <div
      className={`flex flex-col overflow-hidden h-[calc(100vh-64px)] ${
        isDarkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-800"
      }`}
    >
      <main className="w-full flex-1 flex flex-col py-3 px-4 lg:px-6 min-h-0 overflow-hidden">
        <motion.div
          className={`${
            isDarkMode
              ? "bg-gray-900 text-white border-gray-700"
              : "bg-gray-100 text-black border-gray-300"
          } backdrop-filter backdrop-blur-lg shadow-lg rounded-xl p-3 border flex-1 flex flex-col min-h-0 overflow-hidden`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-wrap items-center gap-3 mb-3 flex-shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">{t("pages.calendar.view")}:</span>
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                className={`px-2 py-1.5 text-sm rounded border ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-700 text-white"
                    : "bg-white border-gray-300 text-black"
                }`}
              >
                <option value="global">{t("pages.calendar.global")}</option>
                <option value="player">{t("pages.calendar.player")}</option>
              </select>
            </div>

            {scope === "player" && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">{t("pages.calendar.player")}:</span>
                <select
                  value={playerId}
                  onChange={(e) => setPlayerId(e.target.value)}
                  className={`px-2 py-1.5 text-sm rounded border min-w-[200px] ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700 text-white"
                      : "bg-white border-gray-300 text-black"
                  }`}
                >
                  <option value="">{t("pages.calendar.select_player")}</option>
                  {players.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name ||
                        p.full_name ||
                        `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() ||
                        p.id}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Transfermarkt import button */}
            {scope === "global" && (
              <div className="flex items-center gap-2 ml-auto">
                <button
                  onClick={handleImportMatches}
                  disabled={importing}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                    importing
                      ? "opacity-50 cursor-not-allowed"
                      : isDarkMode
                        ? "border-emerald-600 bg-emerald-700/30 text-emerald-300 hover:bg-emerald-700/50"
                        : "border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                  }`}
                  title={t("pages.calendar.import_title")}
                >
                  {importing ? (
                    <>
                      <span className="animate-spin text-xs">⏳</span>
                      {t("pages.calendar.importing")}
                    </>
                  ) : (
                    <>
                      ⚽ {t("pages.calendar.import_matches")}
                    </>
                  )}
                </button>

                {importResult && (
                  <span className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {t("pages.calendar.imported_summary", {
                      imported: importResult.imported,
                      updated: importResult.updated || 0,
                      removed: importResult.removed || 0,
                      skipped: importResult.skipped,
                    })}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="flex-1 grid grid-cols-1 lg:grid-cols-4 gap-3 min-h-0 overflow-hidden">
            <div className="lg:col-span-3 min-h-0 overflow-hidden">
              <ClubCalendar key={calendarKey} scope={scope} playerId={playerId} players={players} />
            </div>

            <div className="lg:col-span-1 min-h-0 overflow-auto">
              <UpcomingActivities />
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default CalendarPage;